import { useState } from "react";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { api, ApiError } from "../lib/api.js";
import { Mostarda } from "../components/Mostarda.js";

/**
 * Entrada por passkey. Sem senha: este banco tem peso, medicação e exame de duas
 * pessoas, e senha reutilizada é o vetor mais provável de comprometimento.
 */
export function Login({ aoEntrar }: { aoEntrar: () => void }) {
  const [modo, setModo] = useState<"entrar" | "cadastrar">("entrar");
  const [convite, setConvite] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function entrar() {
    setErro(null);
    setOcupado(true);
    try {
      const { options, challengeId } = await api.post<{ options: never; challengeId: string }>(
        "/auth/login/begin",
      );
      const response = await startAuthentication({ optionsJSON: options });
      await api.post("/auth/login/finish", { challengeId, response });
      aoEntrar();
    } catch (err) {
      setErro(mensagemDe(err));
    } finally {
      setOcupado(false);
    }
  }

  async function cadastrar() {
    setErro(null);
    setOcupado(true);
    try {
      const { options } = await api.post<{ options: never }>("/auth/register/begin", {
        inviteCode: convite.trim(),
      });
      const response = await startRegistration({ optionsJSON: options });
      await api.post("/auth/register/finish", { inviteCode: convite.trim(), response });
      aoEntrar();
    } catch (err) {
      setErro(mensagemDe(err));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center gap-6 px-5">
      <div className="flex flex-col items-center gap-2 text-center">
        <Mostarda estado="espreitando" tamanho={140} />
        <h1 className="text-3xl font-bold">Mostarda</h1>
        <p className="text-sm text-suave">
          Teu centralizador de saúde. E o gato que não te deixa esquecer.
        </p>
      </div>

      {modo === "cadastrar" && (
        <label className="flex flex-col gap-1.5">
          <span className="rotulo">Código do convite</span>
          <input
            className="rounded-xl2 border border-borda bg-superficie px-4 py-3"
            value={convite}
            onChange={(e) => setConvite(e.target.value)}
            autoComplete="one-time-code"
            placeholder="cole aqui"
          />
        </label>
      )}

      {erro && (
        <p role="alert" className="rounded-xl2 bg-alerta/10 px-4 py-3 text-sm text-alerta">
          {erro}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <button
          className="botao-primario"
          disabled={ocupado || (modo === "cadastrar" && convite.trim().length < 4)}
          onClick={modo === "entrar" ? entrar : cadastrar}
        >
          {ocupado ? "Um segundo…" : modo === "entrar" ? "Entrar com passkey" : "Criar passkey"}
        </button>

        <button
          className="text-sm text-suave underline underline-offset-4"
          onClick={() => {
            setModo(modo === "entrar" ? "cadastrar" : "entrar");
            setErro(null);
          }}
        >
          {modo === "entrar" ? "Tenho um convite" : "Já tenho passkey"}
        </button>
      </div>
    </main>
  );
}

function mensagemDe(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) {
    // O navegador cancela a passkey com NotAllowedError quando a pessoa fecha o
    // diálogo. Não é falha do app, e chamar de erro assusta à toa.
    if (err.name === "NotAllowedError") return "Ficou pela metade. Tenta de novo quando quiser.";
    return err.message;
  }
  return "Não consegui. Tenta de novo.";
}
